<template>
  <section>
    <h1 class="text-h3 mb-8">
      Icons
    </h1>
    <h2 class="text-h4 mb-4">
      Icons provider
    </h2>
    <p>
      Icons library: <a
        href="https://fontawesome.com/v6.0/icons"
        target="_blank"
        rel="noopener noreferrer"
      >Font Awesome v6</a>
    </p>
    <p>
      We use Font Awesome Pro license, so all styles (solid, regular, light, thin, duotone, brands) of icons can be used.<br>
      Icons are in web font format, embedded with <a
        href="https://fontawesome.com/kits"
        target="_blank"
        rel="noopener noreferrer"
      >Font Awesome Kit</a>, besides the built in icons, you can upload custom icons to the Kit as well.
    </p>
    <h2 class="text-h4 mb-4">
      Syntax
    </h2>
    <p>
      Usage: <code>&lt;v-icon>icon code&lt;/v-icon></code><br>
      e.g.
      <v-icon>fa-regular fa-crown</v-icon> <code>&lt;v-icon>fa-regular fa-crown&lt;/v-icon></code>,
      <v-icon>fa-solid fa-crown</v-icon> <code>&lt;v-icon>fa-solid fa-crown&lt;/v-icon></code>
    </p>
    <h2 class="text-h4 mb-4">
      Styles and variations
    </h2>
    <v-sheet
      v-for="style in styles"
      :key="style"
      class="mb-4 px-6 py-4"
      outlined
      rounded
    >
      <div class="text-body-1 grey--text text--darken-1">
        <strong>{{ style.name }}</strong> | Prefix:
        <template
          v-for="(code, i) in style.prefix"
        >
          <code :key="code">{{ code }}</code>
          <span
            v-if="i < style.prefix.length - 1"
            :key="i"
          > or </span>
        </template>
      </div>
      <div class="mt-4">
        <div class="d-flex align-end text-center">
          <div
            v-for="size in sizes"
            :key="size"
            class="px-3"
          >
            <v-icon
              v-bind="{ [size]: true }"
            >
              {{ style.icon }}
            </v-icon>
            <code class="d-block mt-1">{{ size }}</code>
          </div>
        </div>
        <div class="mt-4">
          <v-btn
            class="mr-2"
            color="primary"
            dark
          >
            Accept
            <v-icon
              dark
              right
            >
              {{ style.icon }}
            </v-icon>
          </v-btn>
          <v-btn
            color="red"
            dark
          >
            Decline
            <v-icon
              dark
              right
            >
              {{ style.icon }}
            </v-icon>
          </v-btn>
        </div>
        <div class="mt-4">
          <v-btn
            class="mr-2"
            dark
          >
            <v-icon
              dark
              left
            >
              {{ style.icon }}
            </v-icon>Cancel
          </v-btn>
          <v-btn
            class="mr-2"
            color="teal darken-2"
            dark
          >
            <v-icon
              dark
              left
            >
              {{ style.icon }}
            </v-icon>Back
          </v-btn>
        </div>
        <div class="mt-4">
          <v-btn
            class="mr-2"
            color="secondary"
            dark
          >
            <v-icon
              dark
              left
            >
              {{ style.icon }} fa-spin
            </v-icon>Loading
          </v-btn>
          <v-btn
            class="mr-2"
            color="blue-grey"
            dark
          >
            <v-icon
              dark
              left
            >
              {{ style.icon }} fa-rotate-90
            </v-icon>Rotate me
          </v-btn>
          <v-btn
            class="mr-2"
            color="success"
            dark
          >
            <v-icon
              dark
              left
            >
              {{ style.icon }} fa-beat
            </v-icon>Beating
          </v-btn>
        </div>
        <div class="mt-4">
          <v-btn
            class="mr-2"
            color="purple"
            dark
          >
            <v-icon dark>
              {{ style.icon }}
            </v-icon>
          </v-btn>
          <v-btn
            class="mr-2"
            color="indigo"
            dark
          >
            <v-icon dark>
              {{ style.icon }}
            </v-icon>
          </v-btn>
        </div>
        <div class="mt-4">
          <v-btn
            class="mr-2"
            text
            icon
            color="blue lighten-2"
          >
            <v-icon>{{ style.icon }}</v-icon>
          </v-btn>
          <v-btn
            text
            icon
            color="red lighten-2"
          >
            <v-icon>{{ style.icon }}</v-icon>
          </v-btn>
        </div>
      </div>
      <v-alert
        v-if="style.name === 'Duotone'"
        class="mt-6"
        type="info"
        text
      >
        Duotone is not working properly in web font format due to Vuetify v2 does not compatible with it.<br>
        Although it will work in SVG format, but Vuetify cannot handle SVG format with Kits due to rendering issues.<br>
        It only works well with vue-fontawesome component, but it need to install multiple icon packages, spend more time to manage them, also, we cannot add custom icons in it, so we'd rather just use Kits now. :)<br>
        Note: Duotone will be supported in Vuetify v3.
      </v-alert>
    </v-sheet>
    <h2 class="text-h4 mb-4">
      Further reading
    </h2>
    <external-link href="https://vuetifyjs.com/en/components/icons/">
      <strong>Icons</strong>
    </external-link>
    <external-link href="https://fontawesome.com/v6.0/docs/web/style/styling">
      <strong>Styling with Font Awesome</strong><br>
      Sizes, rotations, animations...
    </external-link>
  </section>
</template>

<script>
export default {
  name: 'ComponentsIcons',
  data: () => ({
    sizes: [
      'x-small',
      'small',
      'dense',
      'large',
      'x-large'
    ],
    styles: [
      {
        name: 'Thin',
        prefix: ['fat', 'fa-thin'],
        icon: 'fa-thin fa-robot'
      },
      {
        name: 'Light',
        prefix: ['fal', 'fa-light'],
        icon: 'fa-light fa-robot'
      },
      {
        name: 'Regular',
        prefix: ['far', 'fa-regular'],
        icon: 'fa-regular fa-robot'
      },
      {
        name: 'Solid',
        prefix: ['fas', 'fa-solid'],
        icon: 'fa-solid fa-robot'
      },
      {
        name: 'Duotone',
        prefix: ['fad', 'fa-duotone'],
        icon: 'fa-duotone fa-robot'
      },
      {
        name: 'Brands',
        prefix: ['fab', 'fa-brands'],
        icon: 'fa-brands fa-fort-awesome'
      },
      {
        name: 'Kit',
        prefix: ['fak'],
        icon: 'fak fa-name-card'
      }
    ]
  })
}
</script>
